import { WorkawareApi } from 'apis/WorkawareApi';
import { PagedArguments, PagedResult } from 'types/apis';
import { FormCompletionModel } from 'data/models/FormCompletionModel';
import { FormCompletionAggregationDto } from 'data/dtos/FormCompletionAggregationDto';
import { DynamicFormCompletionAttachmentsDto } from 'data/dtos/DynamicFormCompletionAttachmentsDto';
import { ParticipationFilter } from 'components/liveReporting/models/ParticipationFilter';
import { CompanyFormModel } from 'data/models';

/** Trimmed completion row used only to work out which form templates are mappable. */
export interface MappableCompletionKey {
  CompanyFormId?: number
  Latitude?: number | null
  Longitude?: number | null
  LocationId?: number | null
}

/** Upper bound on the rows pulled for that check. Only four columns per row, and it
 *  runs once when the Map Layers drawer is first opened -- but it is a cap, so a
 *  tenant with more located completions than this could in principle miss a template
 *  whose only located completions fall beyond it. */
const MAX_MAPPABLE_KEYS = 5000

export class completedFormsService {

  private _api: WorkawareApi;

  constructor(api: WorkawareApi) {
    this._api = api;
  }

   all = () => new Promise<FormCompletionModel>((resolve, reject) => {
    this._api.forms.completed.all()
      .then(result => resolve(result as FormCompletionModel))
      .catch(error => reject(error));
   });
    getByCompany = (companyId: number) =>
        new Promise<FormCompletionModel[]>((resolve, reject) => {

            const filter = `$filter=CompanyId eq ${companyId}`;

            // take=10000 = get many records
            this._api.forms.completed
                .paged({ offset: 0, take: 10000 }, filter)
                .then(result => resolve(result.value ?? result.Values ?? []))
                .catch(error => reject(error));

        });

    /**
     * Fetch completed form submissions for one or more form templates.
     * Coordinate filtering (Latitude/Longitude not null) is done client-side
     * in LocationTracking to avoid OData null-comparison compatibility issues.
     *
     * IDs are split into batches of MAX_IDS_PER_BATCH to stay under the
     * server's OData node-count limit (default 100 nodes).  Each `or`
     * condition in a $filter costs ~4 AST nodes, so 20 IDs ≈ 79 nodes —
     * safely under the cap.  All batches are fired in parallel and their
     * results are merged before resolving.
     */
    getWithLocationByFormIds = (formIds: number[]) =>
        new Promise<FormCompletionModel[]>((resolve, reject) => {
            if (formIds.length === 0) { resolve([]); return; }

            const MAX_IDS_PER_BATCH = 20;

            // Split into chunks of at most MAX_IDS_PER_BATCH
            const batches: number[][] = [];
            for (let i = 0; i < formIds.length; i += MAX_IDS_PER_BATCH) {
                batches.push(formIds.slice(i, i + MAX_IDS_PER_BATCH));
            }

            // Fire all batches in parallel
            Promise.all(
                batches.map(batch => {
                    const idFilter = batch.map(id => `CompanyFormId eq ${id}`).join(' or ');
                    return this._api.forms.completed
                        .paged(
                            { offset: 0, take: 1000 },
                            `$filter=(${idFilter})`
                        )
                        .then((result: any) => result.value ?? result.Values ?? []);
                })
            )
                .then(results => resolve((results as FormCompletionModel[][]).flat()))
                .catch(error => reject(error));
        });




  /**
   * One row per completion that could actually be plotted -- it either carries GPS
   * coordinates or references a site. Used to decide which form TEMPLATES are worth
   * listing in the map's Forms layer: a template whose completions have no location
   * draws nothing, so offering it as a checkbox only invites a pointless click.
   *
   * $select trims the payload to the four fields that matter rather than returning
   * whole completions, and the caller still applies the real mappability test (a
   * LocationId only counts if that site has coordinates -- see siteCoordsRef in
   * LocationTracking).
   */
  getMappableCompletionKeys = () =>
    new Promise<MappableCompletionKey[]>((resolve, reject) => {
      const query =
        '$filter=(Latitude ne null and Longitude ne null) or (LocationId ne null)' +
        '&$select=CompanyFormId,Latitude,Longitude,LocationId'
      this._api.forms.completed
        .paged({ offset: 0, take: MAX_MAPPABLE_KEYS }, query)
        .then((result: any) => resolve(result.value ?? result.Values ?? []))
        .catch(reject)
    })

  paged = (args: PagedArguments, filter?: string) => new Promise<PagedResult<FormCompletionModel>>((resolve, reject) => {
    Promise.all([
      this._api.forms.completed.paged(args, filter),
      this._api.forms.templates.allCompact(),
    ])
      .then(([completionsResult, templatesResult]) => {
        const rows = completionsResult.value ?? completionsResult.Values ?? [];
        const count = completionsResult['@odata.count'] ?? completionsResult.Count ?? 0;

        const templateMap = new Map<number, { Name: string; Color?: string }>();
        (templatesResult.value ?? []).forEach((t: any) => templateMap.set(t.Id, t));

        const enriched = rows.map((r: any) => {
          const template = r.CompanyFormId != null ? templateMap.get(r.CompanyFormId) : undefined;
          const templateName = template?.Name ?? r.TemplateName;
          const submissionName = (r.TemplateName && r.TemplateName !== templateName) ? r.TemplateName : undefined;
          return {
            ...r,
            TemplateName: templateName,
            SubmissionName: submissionName,
            Color: r.Color ?? template?.Color,
          };
        });

        resolve({ Count: count, Values: enriched });
      })
      .catch(error => reject(error));
  });

  countAll = () => new Promise<number>((resolve, reject) => {
    this._api.forms.completed.total()
      .then(result => resolve(+result))
      .catch(error => reject(error))
  });

  setOwner = (form: FormCompletionModel, newOwnerId: number) => new Promise<void>((resolve, reject) => {
    if (form.Id === undefined) {
      reject('form id is not set');
    } else {
      this._api.forms.completed.changeOwner(form.Id, newOwnerId)
        .then(() => resolve())
        .catch(error => reject(error))
    }
  });
  getAll = () => new Promise<CompanyFormModel[]>((resolve, reject) => {
    this._api.forms.templates.getAll()
      .then(form => resolve(form.value))
      .catch(error => reject(error))
  })
  aggregated = (filter: ParticipationFilter) => new Promise<FormCompletionAggregationDto>((resolve, reject) => {
    this._api.forms.completed.aggregated(filter)
      .then(data => resolve(data as FormCompletionAggregationDto))
      .catch(error => reject(error));
  });

  /**
   * The promise returned from this call will resolve to either a 
   * FormCompletionDto or a DynamicFormCompletionDto based om whether the form
   * is 'dynamic' or not
   * 
   * @param id the form completion id to return
   * @returns FormCompletionDto | DynamicFormCompletionDto
   */
  get = (id: number) => new Promise<any>((resolve, reject) => {
    this._api.forms.completed.get(id)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  /**
   * Returns the PDF (details) as a Blob
   * 
   * @param id 
   * @returns A Blop of the PDF data
   */
  details = (id: number) => new Promise<Blob>((resolve, reject) => {
    this._api.forms.detail.get(id)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  /**
   * Save a dynamic form
   *
   * @param templateId  The company form template id
   * @param data        The form field values
   * @param coordinates Optional browser geolocation coordinates captured at submit time
   */
  saveDynamicForm = (templateId: number, data: any, coordinates?: GeolocationCoordinates) => new Promise<any>((resolve, reject) => {
    this._api.forms.dynamic.post({
      CompanyFormId: templateId,
      FormData: JSON.stringify(data),
      ...(coordinates != null && {
        Latitude: coordinates.latitude,
        Longitude: coordinates.longitude,
      }),
    })
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  /**
   * Update a dynamic form
   *
   * @param formCompletionId  The existing form completion id
   * @param data              The updated form field values
   * @param coordinates       Optional browser geolocation coordinates captured at submit time
   */
  updateDynamicForm = (formCompletionId: number, data: any, coordinates?: GeolocationCoordinates) => new Promise<any>((resolve, reject) => {
    this._api.forms.dynamic.put({
      FormCompletionId: formCompletionId,
      FormData: JSON.stringify(data),
      ...(coordinates != null && {
        Latitude: coordinates.latitude,
        Longitude: coordinates.longitude,
      }),
    })
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  /**
   * Get a minimal listing of the form attachment data
   * 
   * @param formCompletionId 
   */
  getDynamicFormAttachments = (formCompletionId: number) => new Promise<DynamicFormCompletionAttachmentsDto>((resolve, reject) => {
    this._api.forms.dynamic.attachments.get(formCompletionId)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  deleteDynamicFormAttachment = (formCompletionId: number, attachmentId: string) => new Promise<DynamicFormCompletionAttachmentsDto>((resolve, reject) => {
    this._api.forms.dynamic.attachments.delete(formCompletionId, attachmentId)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  uploadDynamicFormAttachment = (formCompletionId: number, data: FormData) => new Promise<DynamicFormCompletionAttachmentsDto>((resolve, reject) => {
    this._api.forms.dynamic.attachments.post(formCompletionId, data)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  downloadAttachment = (formId: number, attachmentId: string) => new Promise<Blob>((resolve, reject) => {
    this._api.forms.dynamic.attachments.download(formId, attachmentId)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  downloadAllCompletedForms = (filter: any) => new Promise<Blob>((resolve, reject) => {
    this._api.forms.completed.downloadAll(filter)
      .then(result => resolve(result))
      .catch(error => reject(error))
  });

  delete = (id: number) => new Promise<void>((resolve, reject) => {
    this._api.forms.completed.delete(id)
      .then(() => resolve())
      .catch(error => reject(error))
  });
}